import React from 'react';
import { useAssistantWidget } from '../../contexts/assistant-widget-context';
import { usePeersona } from '../../contexts/peersona-context';
import { useRecentConversations } from '../../hooks/api/useRecentConversations';
import { pickPreviewText } from '../../utils/chat-preview-text';
import { formatVerboseRelativeTime } from '../../utils/relative-time';
import { PeersonaAvatar } from '../peersona/peersona-avatar';
import { PeersonaSparkle } from '../peersona/peersona-sparkle';
import { PEERSONA_TEXT_MUTED } from '../peersona/peersona-styles';
import { ConversationRow } from './conversation-row';
import { FiPinnedRow, ListViewMessage, PeersonasSectionHeader } from './list-view-bits';

/**
 * Conversations history list. Fi pinned on top, then one row per past
 * thread across every eligible Peersona, sorted newest first. Rows
 * deep-link into the specific thread rather than just opening the panel.
 */
export const ConversationsListView: React.FC = () => {
  const { switchContext, openChatView } = useAssistantWidget();
  const { openPanelForChatbotThread } = usePeersona();
  const { isLoading, rows } = useRecentConversations();

  const handleFiClick = () => {
    switchContext({ kind: 'fi' });
    openChatView();
  };

  const handleThreadClick = (chatbotId: number, threadId: number) => {
    openPanelForChatbotThread(chatbotId, threadId);
    openChatView();
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-4">
      <FiPinnedRow body="Ask Fi about anything on the platform" onClick={handleFiClick} />
      <PeersonasSectionHeader />
      {isLoading && rows.length === 0 ? (
        <ListViewMessage>Loading&hellip;</ListViewMessage>
      ) : rows.length === 0 ? (
        <ListViewMessage>No past conversations yet.</ListViewMessage>
      ) : (
        <div className="flex flex-col">
          {rows.map(({ peersona, thread }) => {
            const body = pickPreviewText({
              summary: undefined,
              preview: thread.preview,
              lastMessageAt: thread.updated_at,
            });
            return (
              <ConversationRow
                key={`${peersona.chatbotId}-${thread.thread_id}`}
                avatar={<PeersonaAvatar size={48} src={peersona.avatarUrl} name={peersona.name} />}
                titleLeading={<PeersonaSparkle size={12} style={{ color: PEERSONA_TEXT_MUTED }} />}
                title={peersona.name}
                body={body || ''}
                footerMeta={thread.updated_at ? formatVerboseRelativeTime({ iso: thread.updated_at }) : null}
                ariaLabel={`Open conversation with ${peersona.name}`}
                onClick={() => handleThreadClick(peersona.chatbotId, thread.thread_id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
